import Styles from './NSMenuButton.module.css';
import { useRef, useState } from 'react';
import { CopyToClipboard } from '../CopyToClipboard';
import { IBaseComponentProps } from "../props/IBaseComponentProps";
import { NSBoxTextArea } from '../components/NSBoxTextArea';
import { NSDialog } from '../components/NSDialog';
import { SetTimeouService } from 'namirasoft-core';

export interface NSMenuButtonMenuProps
{
    builtin: {
        delete?: {
            enabled: boolean;
            onDelete: () => void
        };
        copy?: {
            enabled: boolean;
            getValue: () => string;
        };
        fullscreen?: {
            enabled: boolean;
            getValue: () => string;
            setValue: (value: string | null) => void;
        };
    };
    items: NSMenuButtonMenuItemProps[];
}

export interface NSMenuButtonMenuItemProps
{
    title: string, icon: string, href?: string, onClick?: () => void
}

export interface NSMenuButtonProps extends IBaseComponentProps
{
    description?: string;
    menu?: NSMenuButtonMenuProps;
}

export interface NSMenuButtonState
{
    value: string | null;
    info: boolean;
    fullscreen: boolean;
    copied: boolean;
}

export function safeMenuMenuItem(props: { menu?: NSMenuButtonMenuProps }, pusher: (items: NSMenuButtonMenuItemProps[]) => void): NSMenuButtonMenuProps
{
    let menu: NSMenuButtonMenuProps = { builtin: { ...props.menu?.builtin }, items: [...(props.menu?.items ?? [])] };
    pusher(menu.items);
    return menu;
}

export function NSMenuButton(props: NSMenuButtonProps)
{
    let textarea = useRef<NSBoxTextArea>(null);
    const service = useRef<SetTimeouService>(new SetTimeouService());

    let [state, setState] = useState<NSMenuButtonState>({ value: "", info: false, fullscreen: false, copied: false });

    const onCloseInfo = () =>
    {
        setState(prevState => { return { ...prevState, info: false }; });
    };
    const onCloseFullScreen = () =>
    {
        setState(prevState => { return { ...prevState, fullscreen: false }; });
        props.menu?.builtin.fullscreen?.setValue(state.value);
    };

    const onChanged = (e: NSBoxTextArea) =>
    {
        setState(prevState => { return { ...prevState, value: e.getValue() }; });
    }

    let buttons: { title: string, icon: string, href?: string, onClick?: () => void }[] = [];

    if (props.description)
        buttons.push({
            title: "Info", icon: "https://static.namirasoft.com/image/concept/info/white-blue.svg", onClick: () =>
            {
                setState(prev => { return { ...prev, info: true } });
            }
        });
    if (props.menu?.builtin?.delete?.enabled)
        if (props.menu?.builtin?.delete?.onDelete)
            buttons.push({
                title: "Delete", icon: "https://static.namirasoft.com/image/concept/delete/blue.svg", onClick: () =>
                {
                    props.menu?.builtin?.delete?.onDelete?.();
                }
            });
    if (props.menu?.builtin?.copy?.enabled)
        buttons.push({
            title: "Copy", icon: state.copied ? "https://static.namirasoft.com/image/concept/check/on/blue.svg" : "https://static.namirasoft.com/image/concept/copy/blue.svg", onClick: () =>
            {
                try
                {
                    CopyToClipboard.copyToClipboard(props.menu?.builtin?.copy?.getValue() ?? "");
                    setState(prev => { return { ...prev, copied: true } });
                    service.current?.setTimeoutIfNotCalledAgain(() =>
                    {
                        setState(prev => { return { ...prev, copied: false } });
                    }, 3000);
                } catch (error)
                {
                }
            }
        });
    if (props.menu?.builtin?.fullscreen?.enabled)
        buttons.push({
            title: "Full Screen", icon: "https://static.namirasoft.com/image/concept/full-screen/blue.svg", onClick: () =>
            {
                setState(prevState => { return { ...prevState, fullscreen: true, value: props.menu?.builtin?.fullscreen?.getValue() ?? "" }; });
                let focus = () =>
                {
                    if (textarea.current?.textarea.current)
                    {
                        textarea.current.textarea.current.focus({});
                        if (document.activeElement === textarea.current.textarea.current)
                            return;
                    }
                    setTimeout(focus, 10);
                }
                focus();
            }
        });
    if (props.menu?.items)
        buttons.push(...props.menu?.items);
    return (
        <div
            id={props.id}
            className={`${Styles.ns_menu_button_container} ${props.classList?.join(" ") ?? ""}`}
            style={props.style}
        >
            <div className={Styles.ns_menu_button_group_parrent}>
                <div className={Styles.ns_menu_button_group}>
                    <div style={{ direction: "rtl" }}>
                        {
                            buttons.map(button => (
                                button.href ?
                                    <a
                                        title={button.title}
                                        className={Styles.ns_menu_button_menu_item}
                                        href={button.href}
                                        tabIndex={-1}
                                    >
                                        <img
                                            src={button.icon}
                                            alt={button.title}
                                            width={16}
                                            height={16}
                                        />
                                    </a> :
                                    <button
                                        title={button.title}
                                        className={Styles.ns_menu_button_menu_item}
                                        onClick={button.onClick}
                                        tabIndex={-1}
                                    >
                                        <img
                                            src={button.icon}
                                            alt={button.title}
                                            width={16}
                                            height={16}
                                        />
                                    </button>
                            ))
                        }
                    </div>
                </div>
                {
                    state.info &&
                    <NSDialog
                        onClose={() => { onCloseInfo() }}
                        center_dialog
                        closeButton={{ out: true }}
                    >
                        {
                            props.description
                        }
                    </NSDialog>
                }
                {
                    state.fullscreen &&
                    <NSDialog
                        onClose={() => { onCloseFullScreen() }}
                        center_dialog
                        closeButton={{ out: true }}
                        style={{ width: "80%", height: "80%", maxWidth: "100%", maxHeight: "100%" }}
                    >
                        <NSBoxTextArea
                            ref={textarea}
                            required={false}
                            title=''
                            hideHeader
                            defaultValue={props.menu?.builtin?.fullscreen?.getValue()}
                            onChanged={onChanged}
                            style={{ width: "100%", height: "100%", maxWidth: "100%", maxHeight: "100%", zIndex: "999" }}
                        ></NSBoxTextArea>
                    </NSDialog>
                }
            </div>
        </div>
    );
}